import { zodResolver } from '@hookform/resolvers/zod';
import { api } from '@packages/backend/convex/_generated/api';
import { getUserErrorMessage } from '@packages/shared';
import { createFileRoute } from '@tanstack/react-router';
import { useConvex } from 'convex/react';
import { useEffect } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { toast } from 'sonner';
import { ActionButton } from '@/components/ActionButton';
import { FormWrapper } from '@/components/FormWrapper';
import { handleSubmitErrors } from '@/lib/handleSubmitErrors';
import { useRestaurant } from '@/routes/restaurant/useRestaurantData';
import { OnlinePaymentsCard } from './OnlinePaymentsCard';
import { OtherSettingsCard } from './OtherSettingsCard';
import {
  type PlatformSettingsFormInput,
  type PlatformSettingsFormValues,
  platformSettingsFormValuesSchema,
} from './platformSettingsSchemas';

export const Route = createFileRoute('/r/$restaurantId/s/platform-settings')({
  component: PlatformSettingsPage,
});

function PlatformSettingsPage() {
  const { restaurantId } = Route.useParams();
  const restaurant = useRestaurant();
  const convex = useConvex();
  const form = useForm<PlatformSettingsFormInput, unknown, PlatformSettingsFormValues>({
    defaultValues: platformSettingsFormValuesSchema.decode(restaurant),
    resolver: zodResolver(platformSettingsFormValuesSchema),
  });
  const onlinePaymentTypes = useWatch({ control: form.control, name: 'onlinePaymentTypes' });
  const hasCreditCard = onlinePaymentTypes?.includes('CREDIT_CARD') ?? false;

  // Subscribe to changes
  useEffect(() => {
    // Ensure un-saved user changes are kept on Convex sync
    form.reset(platformSettingsFormValuesSchema.decode(restaurant), {
      keepDirty: true,
      keepDirtyValues: true,
    });
  }, [form, restaurant]);

  const onValidSubmit = async (formValues: PlatformSettingsFormValues) => {
    try {
      await convex.mutation(api.restaurants.savePlatformSettings, {
        formValues,
        restaurantId,
      });
      // The saved values are the new baseline, so nothing is dirty any more
      form.reset(form.getValues());
      toast.success('Saved successfully');
    } catch (error) {
      toast.error('Error saving!', {
        description: getUserErrorMessage(error),
      });
    }
  };

  return (
    <div className="space-y-4">
      <FormWrapper {...form}>
        <form
          id="platform-settings-form"
          onSubmit={form.handleSubmit(onValidSubmit, handleSubmitErrors)}
        >
          <div className="space-y-4">
            <OtherSettingsCard form={form} />
            {hasCreditCard && <OnlinePaymentsCard form={form} />}
          </div>
        </form>
      </FormWrapper>

      <ActionButton
        disabled={form.formState.isSubmitting || !form.formState.isDirty}
        form="platform-settings-form"
        loading={form.formState.isSubmitting}
        type="submit"
      >
        Save
      </ActionButton>
    </div>
  );
}
